import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-solid text-text-on-brand hover:bg-brand-solid-hover border border-transparent shadow-sm',
  secondary:
    'bg-surface-raised text-text-primary border border-border-strong hover:bg-surface-hover',
  ghost: 'bg-transparent text-text-primary border border-transparent hover:bg-surface-hover',
  danger: 'bg-state-critical-surface text-state-critical border border-state-critical-border hover:brightness-110',
  link: 'bg-transparent text-text-link underline underline-offset-4 hover:text-brand-primary-hover border border-transparent px-0',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm gap-1.5',
  md: 'h-10 px-4 text-body-sm gap-2',
  lg: 'h-11 px-5 text-body gap-2',
}

interface BaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Render as the single child element (e.g. a router <Link>) keeping button styling. */
  asChild?: boolean
  /** Shows a spinner, sets aria-busy, and blocks activation without changing layout width. */
  loading?: boolean
  loadingLabel?: string
  iconStart?: ReactNode
  iconEnd?: ReactNode
}

/**
 * An icon-only button has no accessible name from its content, so the type
 * system demands one. This is the cheapest defence against the most common
 * serious axe violation in an icon-dense admin UI (AC-56).
 */
type ButtonProps =
  | (BaseProps & { children: ReactNode })
  | (BaseProps & { children?: undefined; 'aria-label': string })

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    asChild = false,
    loading = false,
    loadingLabel = 'Working…',
    iconStart,
    iconEnd,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button'
  const isDisabled = disabled || loading

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={asChild ? undefined : isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={asChild && isDisabled ? true : undefined}
      data-loading={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-md font-medium',
        'transition-colors duration-fast ease-standard',
        'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        focusRing,
        className,
      )}
      {...rest}
    >
      {loading ? (
        <>
          <Loader2 aria-hidden className="size-4 animate-spin" />
          <span className="sr-only">{loadingLabel}</span>
          <span aria-hidden>{children}</span>
        </>
      ) : (
        <>
          {iconStart}
          {children}
          {iconEnd}
        </>
      )}
    </Comp>
  )
})
